import React, { useState, useMemo } from 'react';
import { 
  Card, Table, Typography, Tabs, Tag, Tooltip, Button, 
  Statistic, Row, Col, Alert, Space, Divider, Select, 
  Progress, Collapse, Empty, Slider, InputNumber, Descriptions,
  Badge, Modal, Radio
} from 'antd';
import { 
  ClockCircleOutlined, 
  ScheduleOutlined, 
  FieldTimeOutlined,
  CarOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
  DollarOutlined,
  CalculatorOutlined,
  LineChartOutlined,
  ExperimentOutlined
} from '@ant-design/icons';

// Import recharts components
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

import { HybridAnalysis } from './HybridAnalysis';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Option } = Select;
const { RadioGroup } = Radio;

// Polyfill for Math.erf if not available
if (!Math.erf) {
  Math.erf = function(x) {
    // Abramowitz and Stegun formula 7.1.26
    // with maximal error of 1.5 × 10−7
    const sign = (x >= 0) ? 1 : -1;
    x = Math.abs(x);
    const a1 =  0.254829592;
    const a2 = -0.284496736;
    const a3 =  1.421413741;
    const a4 = -1.453152027;
    const a5 =  1.061405429;
    const p  =  0.3275911;
    const t = 1.0/(1.0 + p*x);
    const y = 1.0 - (((((a5*t + a4)*t) + a3)*t + a2)*t + a1)*t*Math.exp(-x*x);
    return sign*y;
  };
}

// Helper function to calculate z-score from percentile
// This is an approximation of the inverse normal CDF
const calculateZScore = (percentile) => {
  // Abramowitz and Stegun approximation for the inverse normal CDF
  // This is a good approximation for percentiles between 0.01 and 0.99
  
  // Constants for the approximation
  const a1 = -3.969683028665376e+01;
  const a2 = 2.209460984245205e+02;
  const a3 = -2.759285104469687e+02;
  const a4 = 1.383577518672690e+02;
  const a5 = -3.066479806614201e+01;
  const a6 = 2.506628277459239e+00;
  
  const b1 = -5.447609879822406e+01;
  const b2 = 1.615858368580409e+02;
  const b3 = -1.556989798598866e+02;
  const b4 = 6.680131188771972e+01;
  const b5 = -1.328068155288572e+01;
  
  const c1 = -7.784894002430293e-03;
  const c2 = -3.223964580411365e-01;
  const c3 = -2.400758277161838e+00;
  const c4 = -2.549732539343734e+00;
  const c5 = 4.374664141464968e+00;
  const c6 = 2.938163982698783e+00;
  
  const d1 = 7.784695709041462e-03;
  const d2 = 3.224671290700398e-01;
  const d3 = 2.445134137142996e+00;
  const d4 = 3.754408661907416e+00;
  
  // Define the regions
  const p_low = 0.02425;
  const p_high = 1 - p_low;
  
  let q, r, z;
  
  // Rational approximation for the lower region
  if (percentile < p_low) {
    q = Math.sqrt(-2 * Math.log(percentile));
    z = (((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) / 
        ((((d1 * q + d2) * q + d3) * q + d4) * q + 1);
  }
  // Rational approximation for the upper region
  else if (percentile > p_high) {
    q = Math.sqrt(-2 * Math.log(1 - percentile));
    z = -(((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) / 
         ((((d1 * q + d2) * q + d3) * q + d4) * q + 1);
  }
  // Rational approximation for the central region
  else {
    q = percentile - 0.5;
    r = q * q;
    z = (((((a1 * r + a2) * r + a3) * r + a4) * r + a5) * r + a6) * q / 
        (((((b1 * r + b2) * r + b3) * r + b4) * r + b5) * r + 1);
  }
  
  return z;
};

// Helper: Standard normal CDF
function normalCDF(x) {
  return 0.5 * (1 + Math.erf(x / Math.sqrt(2)));
}

// Helper: Find shift for normal distribution so P(a <= X-shift <= b) = otrTarget
function findNormalOTRShift(stdDev, otrTarget, earlyBound = -0.5, lateBound = 2) {
  let left = -10, right = 10, bestShift = 0, minDiff = Infinity;
  for (let i = 0; i < 100; i++) {
    const mid = (left + right) / 2;
    const p = normalCDF((lateBound + mid) / stdDev) - normalCDF((earlyBound + mid) / stdDev);
    const diff = Math.abs(p - otrTarget);
    if (diff < minDiff) {
      minDiff = diff;
      bestShift = mid;
    }
    if (p < otrTarget) {
      left = mid;
    } else {
      right = mid;
    }
  }
  return Math.round(bestShift);
}

// Restore: Helper to compute the optimal schedule shift for a given OTR window (empirical)
function computeOTRWindowAdjustment(variances, otrTarget, earlyBound = -0.5, lateBound = 2) {
  if (!variances || variances.length === 0) return 0;
  let bestShift = 0;
  let bestOTR = 0;
  let minDiff = Infinity;
  for (let shift = -10; shift <= 10; shift += 0.1) {
    const inWindow = variances.filter(v => (v - shift) >= earlyBound && (v - shift) <= lateBound).length;
    const otr = inWindow / variances.length;
    const diff = Math.abs(otr - otrTarget);
    if (diff < minDiff || (diff === minDiff && otr > bestOTR)) {
      minDiff = diff;
      bestOTR = otr;
      bestShift = shift;
    }
  }
  return Math.round(bestShift);
}

export const STATISTICAL_MODELS = [
  {
    id: 'normal',
    name: 'Normal Distribution',
    description: 'Assumes arrival times follow a normal (Gaussian) distribution',
    formula: 'f(x) = (1/σ√2π) * e^(-(x-μ)²/2σ²)',
    explanation: 'Most common distribution for arrival times. Symmetric around the mean.',
    calculateAdjustment: (stdDevMinutes, targetPercentile, variances) => {
      if (!stdDevMinutes || stdDevMinutes === 0) return 0;
      return findNormalOTRShift(stdDevMinutes, targetPercentile);
    },
    generateDistributionData: (stop) => {
      const stdDev = stop.adjustments?.stdDev || stop.stdDev;
      if (!stdDev) return [];

      const mean = 0; // Center at 0 for visualization
      const points = 200; // Increased number of points for smoother curve
      const range = 4 * stdDev; // Show ±4 standard deviations
      const step = range / points;

      return Array.from({ length: points }, (_, i) => {
        const x = -range/2 + i * step;
        const density = (1 / (stdDev * Math.sqrt(2 * Math.PI))) * 
                       Math.exp((-((x - mean) ** 2)) / (2 * stdDev ** 2));
        
        // Only mark current and proposed times at specific points
        const isCurrentTime = Math.abs(x) < step/2;
        const isProposedTime = Math.abs(x - (-stdDev)) < step/2;
        
        return {
          time: x,
          density,
          currentTime: isCurrentTime ? density : null,
          proposedTime: isProposedTime ? density : null
        };
      });
    }
  },
  {
    id: 'lognormal',
    name: 'Log-Normal Distribution',
    description: 'Assumes the logarithm of arrival times follows a normal distribution',
    formula: 'f(x) = (1/xσ√2π) * e^(-(ln(x)-μ)²/2σ²)',
    explanation: 'Useful when arrival times are always positive and skewed right.',
    calculateAdjustment: (stdDevMinutes, targetPercentile) => {
      const zScore = calculateZScore(targetPercentile);
      // Convert standard deviation to log-space parameters
      const sigma = Math.sqrt(Math.log(1 + (stdDevMinutes/60)**2)); // Convert to hours for stability
      const logAdjustment = zScore * sigma;
      return Math.round((Math.exp(logAdjustment) - 1) * 60); // Convert back to minutes
    },
    generateDistributionData: (stop) => {
      const stdDev = stop.adjustments?.stdDev || stop.stdDev;
      if (!stdDev) return [];

      const points = 200; // Increased number of points for smoother curve
      const range = 4 * stdDev; // Show ±4 standard deviations
      const step = range / points;

      return Array.from({ length: points }, (_, i) => {
        const x = -range/2 + i * step;
        const density = (1 / (x * stdDev * Math.sqrt(2 * Math.PI))) * 
                       Math.exp((-((Math.log(x)) ** 2)) / (2 * stdDev ** 2));
        
        // Only mark current and proposed times at specific points
        const isCurrentTime = Math.abs(x) < step/2;
        const isProposedTime = Math.abs(x - (-stdDev)) < step/2;
        
        return {
          time: x,
          density,
          currentTime: isCurrentTime ? density : null,
          proposedTime: isProposedTime ? density : null
        };
      });
    }
  },
  {
    id: 'weibull',
    name: 'Weibull Distribution',
    description: 'Flexible distribution that can model various shapes of arrival time distributions',
    formula: 'f(x) = (k/λ)(x/λ)^(k-1)e^(-(x/λ)^k)',
    explanation: 'Good for modeling arrival times with varying reliability patterns.',
    calculateAdjustment: (stdDevMinutes, targetPercentile) => {
      const k = 2; // Shape parameter
      const lambda = stdDevMinutes * Math.sqrt(2/Math.PI);
      // For Weibull, the inverse CDF is: lambda * (-ln(1-p))^(1/k)
      const adjustment = -lambda * Math.pow(-Math.log(targetPercentile), 1/k);
      return Math.round(adjustment);
    },
    generateDistributionData: (stop) => {
      const stdDev = stop.adjustments?.stdDev || stop.stdDev;
      if (!stdDev) return [];

      const k = 2; // Shape parameter
      const lambda = stdDev / Math.sqrt(Math.PI/2);
      const points = 200; // Increased number of points for smoother curve
      const range = 4 * stdDev; // Show ±4 standard deviations
      const step = range / points;

      return Array.from({ length: points }, (_, i) => {
        const x = i * step;
        const density = (k/lambda) * Math.pow(x/lambda, k-1) * 
                       Math.exp(-Math.pow(x/lambda, k));
        
        // Only mark current and proposed times at specific points
        const isCurrentTime = Math.abs(x) < step/2;
        const isProposedTime = Math.abs(x - lambda) < step/2;
        
        return {
          time: x,
          density,
          currentTime: isCurrentTime ? density : null,
          proposedTime: isProposedTime ? density : null
        };
      });
    }
  },
  {
    id: 'empirical',
    name: 'Empirical Percentile',
    description: 'Uses actual historical data percentiles without assuming a distribution',
    formula: 'P(X ≤ x) = p',
    explanation: 'Non-parametric approach using actual data distribution.',
    calculateAdjustment: (stdDevMinutes, targetPercentile, variances) => {
      return computeOTRWindowAdjustment(variances, targetPercentile);
    },
    generateDistributionData: (stop) => {
      const stdDev = stop.adjustments?.stdDev || stop.stdDev;
      if (!stdDev) return [];

      // For empirical distribution, we'll use a kernel density estimation
      const points = 200; // Increased number of points for smoother curve
      const range = 4 * stdDev; // Show ±4 standard deviations
      const step = range / points;
      const bandwidth = stdDev / 2; // Silverman's rule of thumb

      return Array.from({ length: points }, (_, i) => {
        const x = -range/2 + i * step;
        const density = (1 / (bandwidth * Math.sqrt(2 * Math.PI))) * 
                       Math.exp(-(x ** 2) / (2 * bandwidth ** 2));
        
        // Only mark current and proposed times at specific points
        const isCurrentTime = Math.abs(x) < step/2;
        const isProposedTime = Math.abs(x - (-stdDev * 0.8)) < step/2;
        
        return {
          time: x,
          density,
          currentTime: isCurrentTime ? density : null,
          proposedTime: isProposedTime ? density : null
        };
      });
    }
  }
];

/**
 * ProposedTimetables Component
 * Displays proposed timetable adjustments and shift envelope analysis
 */
const ProposedTimetables = ({ reportData }) => {
  // State for filtering and sorting - moved before any conditional returns
  const [selectedRoute, setSelectedRoute] = useState('all');
  const [selectedTrip, setSelectedTrip] = useState('all');
  const [selectedOTR, setSelectedOTR] = useState(95);
  const [sortByImpact, setSortByImpact] = useState(true);
  const [selectedStop, setSelectedStop] = useState(null);
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  
  // Generate thresholds between min and max - moved before any conditional returns
  const thresholds = useMemo(() => {
    return [selectedOTR];
  }, [selectedOTR]);
  
  // Calculate summary statistics for all thresholds - moved before any conditional returns
  const summaries = useMemo(() => {
    if (!reportData || !reportData.proposedTimetables || !reportData.proposedTimetables.proposedTimetables) {
      return [];
    }
    
    // Get all available thresholds from the data
    const availableThresholds = new Set();
    reportData.proposedTimetables.proposedTimetables.forEach(item => {
      item.proposedAdjustments.forEach(adj => {
        availableThresholds.add(adj.target);
      });
    });
    
    // Get the raw data for calculations
    const rawData = reportData.proposedTimetables.proposedTimetables;
    
    return thresholds
      .map(threshold => {
        if (!rawData.length) return null;
        
        // Calculate adjustments for this specific threshold
        const calculatedAdjustments = rawData.map(item => {
          // Find the closest available threshold for reference
          const closestThreshold = Array.from(availableThresholds).reduce((prev, curr) => {
            return Math.abs(curr - threshold) < Math.abs(prev - threshold) ? curr : prev;
          });
          
          // Get the reference adjustment
          const referenceAdjustment = item.proposedAdjustments.find(adj => adj.target === closestThreshold);
          
          if (!referenceAdjustment) return null;
          
          // Get the standard deviation in minutes
          const stdDevMinutes = (item.stdDev || referenceAdjustment.stdDev) / 60;
          
          if (!stdDevMinutes) return null;
          
          // Calculate the target percentile
          const targetPercentile = threshold === 100 ? 0.001 : threshold / 100;
          
          // Calculate adjustments for each model
          const modelAdjustments = STATISTICAL_MODELS.map(model => ({
            modelId: model.id,
            adjustmentMinutes: model.calculateAdjustment(
              stdDevMinutes,
              targetPercentile,
              (model.id === 'normal' || model.id === 'empirical') ? (item.variancesMinutes || []) : undefined
            )
          }));
          
          // Add customModelTime logic
          const getModelAdjustment = (id) => {
            const found = modelAdjustments.find(m => m.modelId === id);
            return found ? found.adjustmentMinutes : null;
          };
          const empiricalAdj = getModelAdjustment('empirical');
          const lognormalAdj = getModelAdjustment('lognormal');
          const weibullAdj = getModelAdjustment('weibull');
          let customModelAdj = modelAdjustments.reduce((sum, m) => sum + m.adjustmentMinutes, 0) / modelAdjustments.length;
          if (empiricalAdj !== null && lognormalAdj !== null && Math.abs(empiricalAdj - lognormalAdj) <= 1) {
            customModelAdj = lognormalAdj;
          } else if (weibullAdj !== null && empiricalAdj !== null && (weibullAdj - empiricalAdj) > 2) {
            customModelAdj = null; // flag as delay zone
          }
          
          // Create the calculated adjustment
          const calculatedAdjustment = {
            ...referenceAdjustment,
            target: threshold,
            modelAdjustments,
            customModelAdj,
            projectedOTR: threshold, // The projected OTR is the target threshold
            stdDev: item.stdDev || referenceAdjustment.stdDev
          };
          
          return {
            ...item,
            calculatedAdjustment
          };
        }).filter(Boolean);
        
        if (calculatedAdjustments.length === 0) return null;
        
        const totalStops = calculatedAdjustments.length;
        const stopsWithAdjustments = calculatedAdjustments.filter(item => 
          item.calculatedAdjustment.modelAdjustments.some(m => m.adjustmentMinutes !== 0)
        ).length;
        
        const avgAdjustment = calculatedAdjustments.reduce((sum, item) => 
          sum + item.calculatedAdjustment.modelAdjustments.reduce((modelSum, m) => 
            modelSum + m.adjustmentMinutes, 0
          ) / STATISTICAL_MODELS.length, 0
        ) / totalStops;
        
        const maxAdjustment = Math.max(...calculatedAdjustments.map(item => 
          Math.max(...item.calculatedAdjustment.modelAdjustments.map(m => 
            Math.abs(m.adjustmentMinutes)
          ))
        ));
        
        // Calculate average standard deviation
        let avgStdDev = 0;
        let hasStdDevData = false;
        
        if (calculatedAdjustments.some(item => item.calculatedAdjustment.stdDev !== undefined)) {
          avgStdDev = calculatedAdjustments.reduce((sum, item) => {
            if (item.calculatedAdjustment.stdDev !== undefined) {
              hasStdDevData = true;
              return sum + item.calculatedAdjustment.stdDev;
            }
            return sum;
          }, 0) / totalStops;
        } 
        else if (rawData.some(item => item.stdDev !== undefined)) {
          avgStdDev = rawData.reduce((sum, item) => {
            if (item.stdDev !== undefined) {
              hasStdDevData = true;
              return sum + item.stdDev;
            }
            return sum;
          }, 0) / rawData.length;
        }
        
        return {
          threshold,
          totalStops,
          stopsWithAdjustments,
          avgAdjustment,
          maxAdjustment,
          avgStdDev,
          hasStdDevData,
          adjustmentPercentage: (stopsWithAdjustments / totalStops) * 100,
          calculatedAdjustments
        };
      })
      .filter(Boolean);
  }, [thresholds, reportData]);
  
  // If no data, show empty state
  if (!reportData || !reportData.proposedTimetables) {
    return (
      <Card title="Proposed Revised Timetables">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No data available. Please generate a report.</Text>
        </div>
      </Card>
    );
  }

  const { proposedTimetables } = reportData;
  
  // Get unique routes for filter dropdown
  const routes = [...new Set(proposedTimetables.proposedTimetables.map(item => item.routeNo))].sort();
  
  // Filter timetables based on selected route
  const filteredTimetables = selectedRoute === 'all' 
    ? proposedTimetables.proposedTimetables 
    : proposedTimetables.proposedTimetables.filter(item => item.routeNo === selectedRoute);
  
  console.log('Filtered timetables for trip options:', filteredTimetables);
  
  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    return timeStr;
  };
  
  // Format adjustment for display
  const formatAdjustment = (minutes) => {
    if (minutes === 0) return 'No change';
    
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    
    let formatted = '';
    if (hours > 0) {
      formatted += `${hours}h `;
    }
    if (mins > 0 || hours === 0) {
      formatted += `${mins}m`;
    }
    
    return minutes > 0 ? `+${formatted}` : `-${formatted}`;
  };
  
  // Get color for OTR percentage
  const getOtrColor = (percentage) => {
    if (percentage >= 95) return 'success';
    if (percentage >= 90) return 'warning';
    return 'error';
  };
  
  // Get color for adjustment
  const getAdjustmentColor = (minutes) => {
    if (minutes === 0) return 'default';
    if (minutes > 0) return 'blue';
    return 'red';
  };
  
  // Generate distribution data for a stop
  const generateDistributionData = (stop) => {
    const stdDev = stop.adjustments?.stdDev || stop.stdDev;
    if (!stdDev) return [];

    const mean = 0; // Center at 0 for visualization
    const points = 200; // Increased number of points for smoother curve
    const range = 4 * stdDev; // Show ±4 standard deviations
    const step = range / points;

    return Array.from({ length: points }, (_, i) => {
      const x = -range/2 + i * step;
      const density = (1 / (stdDev * Math.sqrt(2 * Math.PI))) * 
                     Math.exp((-((x - mean) ** 2)) / (2 * stdDev ** 2));
      
      // Only mark current and proposed times at specific points
      const isCurrentTime = Math.abs(x) < step/2;
      const isProposedTime = Math.abs(x - (-stdDev)) < step/2;
      
      return {
        time: x,
        density,
        currentTime: isCurrentTime ? density : null,
        proposedTime: isProposedTime ? density : null
      };
    });
  };
  
  // Calculate shift envelope impact
  const calculateShiftEnvelopeImpact = (shift) => {
    if (!shift || !shift.adjustments) return null;
    
    // Get the 95% and 98% adjustments
    const adj95 = shift.adjustments['95'] || { totalAdjustmentMinutes: 0, affectedStops: 0 };
    const adj98 = shift.adjustments['98'] || { totalAdjustmentMinutes: 0, affectedStops: 0 };
    
    // Calculate cost implications
    const hourlyRate = 30; // Example hourly rate
    const minutesPerHour = 60;
    
    const cost95 = (adj95.totalAdjustmentMinutes / minutesPerHour) * hourlyRate;
    const cost98 = (adj98.totalAdjustmentMinutes / minutesPerHour) * hourlyRate;
    
    return {
      cost95,
      cost98,
      adj95,
      adj98
    };
  };
  
  // Columns for the timetables table
  const getColumnsForThreshold = (threshold) => {
    // Helper function to get trip start time
    const getTripStartTime = (tripId) => {
      // Find all stops for this trip
      const tripStops = proposedTimetables.proposedTimetables.filter(item => 
        item.tripId && item.tripId.split(',').includes(tripId)
      );
      
      // Find the earliest scheduled time
      const earliestStop = tripStops.reduce((earliest, stop) => {
        if (!earliest) return stop;
        return stop.scheduledTime < earliest.scheduledTime ? stop : earliest;
      }, null);
      
      if (!earliestStop || !earliestStop.scheduledTime) return null;
      
      const timePart = earliestStop.scheduledTime.split(' ')[1];
      if (!timePart) return null;
      
      const [hours, minutes] = timePart.split(':').map(Number);
      return { hours, minutes };
    };

    return [
      {
        title: 'Route',
        dataIndex: 'routeNo',
        key: 'routeNo',
        width: 100,
        render: (text) => <Tag color="blue">{text}</Tag>
      },
      {
        title: 'Trip',
        dataIndex: 'tripId',
        key: 'tripId',
        width: 180,
        sorter: (a, b) => {
          // Get start times for comparison
          const getStartTimeInMinutes = (record) => {
            if (!record.tripId) return 0;
            const tripIds = record.tripId.split(',');
            const startTimes = tripIds.map(id => {
              const time = getTripStartTime(id);
              return time ? time.hours * 60 + time.minutes : 0;
            });
            return Math.min(...startTimes);
          };
          return getStartTimeInMinutes(a) - getStartTimeInMinutes(b);
        },
        render: (text, record) => {
          if (!text) return <Text type="secondary">N/A</Text>;
          const tripIds = text.split(',');
          
          return (
            <Space direction="vertical" size="small">
              {tripIds.map(id => {
                const startTime = getTripStartTime(id);
                return (
                  <Tag key={id} color="purple">
                    {startTime ? `${startTime.hours}:${startTime.minutes.toString().padStart(2, '0')}` : `Trip ${id}`}
                  </Tag>
                );
              })}
            </Space>
          );
        }
      },
      {
        title: 'Stop',
        dataIndex: 'stopId',
        key: 'stopId',
        width: 100,
        render: (text, record) => (
          <Tooltip title={record.stopName}>
            <Text>{text}</Text>
          </Tooltip>
        )
      },
      {
        title: 'Current OTR',
        dataIndex: 'currentOTR',
        key: 'currentOTR',
        width: 120,
        render: (value) => (
          <Progress 
            percent={Math.round(value)} 
            size="small" 
            status={value >= 95 ? 'success' : value >= 90 ? 'normal' : 'exception'}
            format={percent => `${percent.toFixed(1)}%`}
          />
        )
      },
      {
        title: 'Std Dev',
        dataIndex: 'stdDev',
        key: 'stdDev',
        width: 100,
        render: (value) => (
          <Tooltip title="Standard deviation of arrival/departure times">
            <Text>{Math.round(value / 60)} min</Text>
          </Tooltip>
        )
      },
      {
        title: `Proposed Adjustment (${threshold}% OTR)`,
        key: 'adjustment',
        width: 200,
        render: (_, record) => {
          // Find the summary for this threshold
          const summary = summaries.find(s => s.threshold === threshold);
          if (!summary) return <Text type="secondary">No data available</Text>;
          
          // Find the calculated adjustment for this record
          const calculatedItem = summary.calculatedAdjustments.find(
            item => item.routeNo === record.routeNo && item.stopId === record.stopId
          );
          
          if (!calculatedItem) return <Text type="secondary">No data available</Text>;
          
          const adjustment = calculatedItem.calculatedAdjustment;
          
          return (
            <Space direction="vertical" size="small">
              <Tag color={getAdjustmentColor(adjustment.modelAdjustments[0].adjustmentMinutes)}>
                {formatAdjustment(adjustment.modelAdjustments[0].adjustmentMinutes)}
              </Tag>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Projected OTR: {adjustment.projectedOTR.toFixed(1)}%
              </Text>
            </Space>
          );
        }
      },
      {
        title: 'Current Time',
        dataIndex: 'scheduledTime',
        key: 'scheduledTime',
        width: 120,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => {
          const getTimeInMinutes = (timeStr) => {
            if (!timeStr) return 0;
            const timePart = timeStr.split(' ')[1];
            if (!timePart) return 0;
            const [hours, minutes] = timePart.split(':').map(Number);
            return hours * 60 + minutes;
          };
          return getTimeInMinutes(a.scheduledTime) - getTimeInMinutes(b.scheduledTime);
        },
        render: (text) => {
          if (!text) return <Text type="secondary">N/A</Text>;
          const timePart = text.split(' ')[1];
          if (!timePart) return <Text type="secondary">N/A</Text>;
          const [hours, minutes] = timePart.split(':').map(Number);
          return <Text>{`${hours}:${minutes.toString().padStart(2, '0')}`}</Text>;
        }
      },
      {
        title: 'Proposed Time', 
        dataIndex: 'proposedTime',
        key: 'proposedTime',
        width: 120,
        render: (_, record) => {
          const timePart = record.scheduledTime.split(' ')[1];
          if (!timePart) return <Text type="secondary">N/A</Text>;
          let [hours, minutes] = timePart.split(':').map(Number);
           // Find the summary for this threshold
           const summary = summaries.find(s => s.threshold === threshold);
           if (!summary) return <Text type="secondary">No data available</Text>;
           
           // Find the calculated adjustment for this record
           const calculatedItem = summary.calculatedAdjustments.find(
             item => item.routeNo === record.routeNo && item.stopId === record.stopId
           );
           
           if (!calculatedItem) return <Text type="secondary">No data available</Text>;
           
           const adjustment = calculatedItem.calculatedAdjustment;
          hours += Math.floor(adjustment.modelAdjustments[0].adjustmentMinutes / 60);
          minutes += adjustment.modelAdjustments[0].adjustmentMinutes % 60;
          // Handle minute overflow
          if (minutes >= 60) {
            hours += Math.floor(minutes / 60);
            minutes = minutes % 60;
          }
          // Handle negative minutes
          if (minutes < 0) {
            hours -= Math.ceil(Math.abs(minutes) / 60);
            minutes = (minutes % 60 + 60) % 60;
          }
           
          return <Text>{`${hours}:${minutes.toString().padStart(2, '0')}`}</Text>;
        }
      },
      {
        title: 'Trips Affected',
        dataIndex: 'tripCount',
        key: 'tripCount',
        width: 120,
        render: (value) => <Text>{value}</Text>
      },
      {
        title: 'Shifts Affected',
        dataIndex: 'shiftCount',
        key: 'shiftCount',
        width: 120,
        render: (value) => <Text>{value}</Text>
      },
      {
        title: 'Distribution',
        key: 'distribution',
        width: 100,
        render: (_, record) => (
          <Button 
            type="link" 
            icon={<LineChartOutlined />} 
            onClick={() => {
              setSelectedStop(record);
              setShowDistributionModal(true);
            }}
          >
            View
          </Button>
        )
      },
      {
        title: 'Recommended Adj',
        key: 'customModelAdj',
        width: 160,
        render: (_, record) => {
          // Find the summary for this threshold
          const summary = summaries.find(s => s.threshold === threshold);
          if (!summary) return <Text type="secondary">No data available</Text>;
          // Find the calculated adjustment for this record
          const calculatedItem = summary.calculatedAdjustments.find(
            item => item.routeNo === record.routeNo && item.stopId === record.stopId
          );
          if (!calculatedItem) return <Text type="secondary">No data available</Text>;
          const customModelAdj = calculatedItem.calculatedAdjustment.customModelAdj;
          if (customModelAdj === null || isNaN(customModelAdj)) {
            return <Tag color="red">Delay Zone</Tag>;
          }
          return <Tag color="purple">{customModelAdj > 0 ? `+${customModelAdj}` : customModelAdj} min</Tag>;
        }
      }
    ];
  };
  
  // Columns for the shift envelope table
  const shiftColumns = [
    {
      title: 'Shift ID',
      dataIndex: 'shiftId',
      key: 'shiftId',
      width: 120,
      render: (text) => <Tag color="purple">{text}</Tag>
    },
    {
      title: 'Route',
      dataIndex: 'routeNo',
      key: 'routeNo',
      width: 100,
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Trips',
      dataIndex: 'tripCount',
      key: 'tripCount',
      width: 80,
      render: (value) => <Text>{value}</Text>
    },
    {
      title: 'Stops',
      dataIndex: 'stopCount',
      key: 'stopCount',
      width: 80,
      render: (value) => <Text>{value}</Text>
    },
    {
      title: `Total Adjustment (95% OTR)`,
      key: 'adjustment95',
      width: 180,
      render: (_, record) => {
        const impact = calculateShiftEnvelopeImpact(record);
        if (!impact) return <Text>N/A</Text>;
        
        return (
          <Space direction="vertical" size="small">
            <Tag color={getAdjustmentColor(impact.adj95.totalAdjustmentMinutes)}>
              {formatAdjustment(impact.adj95.totalAdjustmentMinutes)}
            </Tag>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {impact.adj95.affectedStops} stops affected
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Cost: ${impact.cost95.toFixed(2)}
            </Text>
          </Space>
        );
      }
    },
    {
      title: `Total Adjustment (98% OTR)`,
      key: 'adjustment98',
      width: 180,
      render: (_, record) => {
        const impact = calculateShiftEnvelopeImpact(record);
        if (!impact) return <Text>N/A</Text>;
        
        return (
          <Space direction="vertical" size="small">
            <Tag color={getAdjustmentColor(impact.adj98.totalAdjustmentMinutes)}>
              {formatAdjustment(impact.adj98.totalAdjustmentMinutes)}
            </Tag>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {impact.adj98.affectedStops} stops affected
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Cost: ${impact.cost98.toFixed(2)}
            </Text>
          </Space>
        );
      }
    }
  ];
  
  // Distribution modal content
  const renderDistributionModal = () => {
    if (!selectedStop) return null;
    
    const distributionData = STATISTICAL_MODELS.map(model => {
      const stdDev = selectedStop.adjustments?.stdDev || selectedStop.stdDev;
      if (!stdDev) return null;
      
      return model.generateDistributionData(selectedStop);
    }).filter(Boolean);

    // Find the summary for the selected OTR threshold
    const summary = summaries.find(s => s.threshold === selectedOTR);
    const calculatedItem = summary?.calculatedAdjustments.find(
      item => item.routeNo === selectedStop.routeNo && item.stopId === selectedStop.stopId
    );
    
    return (
      <Modal
        title={`Time Distribution for Stop ${selectedStop.stopId} (${selectedStop.stopName})`}
        visible={showDistributionModal}
        onCancel={() => setShowDistributionModal(false)}
        width={800}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Route">{selectedStop.routeNo}</Descriptions.Item>
            <Descriptions.Item label="Current Time">{formatTime(selectedStop.scheduledTime)}</Descriptions.Item>
            <Descriptions.Item label="Current OTR">{selectedStop.currentOTR.toFixed(1)}%</Descriptions.Item>
            <Descriptions.Item label="Standard Deviation">{Math.round(selectedStop.stdDev / 60)} minutes</Descriptions.Item>
            <Descriptions.Item label="Target OTR">{selectedOTR}%</Descriptions.Item>
          </Descriptions>
        </div>
        
        <div style={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={distributionData[0]}
              margin={{ top: 20, right: 30, left: 50, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                label={{ value: 'Time (minutes)', position: 'bottom' }}
                tickFormatter={(value) => `${(value/60).toFixed(1)}`}
                domain={['dataMin', 'dataMax']}
              />
              <YAxis 
                label={{ 
                  value: 'Probability Density', 
                  angle: -90, 
                  position: 'insideLeft',
                  offset: -20,
                  style: { textAnchor: 'middle' }
                }}
                tickFormatter={(value) => {
                  const hours = Math.floor(value / (60 * 60 * 1000));
                  const minutes = Math.floor((value % (60 * 60 * 1000)) / (60 * 1000));
                  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                }}
                domain={[0, 'auto']}
              />
              <RechartsTooltip 
                formatter={(value, name) => {
                  if (name === 'Probability Density') return [(value*1000).toFixed(1), name];
                  if (name === 'Current Time' || name === 'Proposed Time') return [(value/60).toFixed(1) ? 'Yes' : 'No', name];
                  return [value, name];
                }}
                labelFormatter={(label) => `Time: ${(label/60).toFixed(1)} minutes`}
              />
              <Legend verticalAlign="top" />
              {STATISTICAL_MODELS.map((model, index) => (
                <Line 
                  key={model.id}
                  type="monotone" 
                  dataKey="density" 
                  stroke={['#8884d8', '#82ca9d', '#ffc658', '#ff8042'][index % 4]} 
                  name={`${model.name} Distribution`}
                  dot={false}
                  strokeWidth={2}
                />
              ))}
              <Line 
                type="monotone" 
                dataKey="currentTime" 
                stroke="#82ca9d" 
                name="Current Time" 
                dot={true}
                strokeWidth={2}
              />
              {STATISTICAL_MODELS.map((model, index) => (
                <Line 
                  key={`${model.id}-proposed`}
                  type="monotone" 
                  dataKey="proposedTime" 
                  stroke={['#8884d8', '#82ca9d', '#ffc658', '#ff8042'][index % 4]} 
                  name={`${model.name} Proposed Time`}
                  dot={true}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        <div style={{ marginTop: 16 }}>
          <Alert
            message="Time Distribution Explanation"
            description={
              <Paragraph>
                This chart shows the probability distributions of arrival/departure times for this stop based on historical data.
                Each colored line represents a different statistical model's interpretation of the data.
                The blue dot represents the current scheduled time, while the colored dots represent the proposed adjusted times for each model.
                The width of each curve indicates the standard deviation (variability) of the arrival/departure times.
                To achieve the target OTR of {selectedOTR}%, each model calculates adjustments using its own statistical approach.
              </Paragraph>
            }
            type="info"
            showIcon
          />
        </div>
      </Modal>
    );
  };
  
  return (
    <div className="proposed-timetables">
      <Card title="Proposed Revised Timetables">
        <Alert
          message="Timetable Adjustment Analysis"
          description={
            <Paragraph>
              This analysis suggests timetable adjustments to achieve on-time performance targets between {selectedOTR}% and {selectedOTR}% based on historical data.
              The "Shift Envelope" tab shows the operational impact of these changes on driver shifts.
            </Paragraph>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <div style={{ marginBottom: 16 }}>
          <Text strong>Statistical Models:</Text>
          <div style={{ marginTop: 8, marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              {STATISTICAL_MODELS.map(model => (
                <Col span={12} key={model.id}>
                  <Card size="small" title={
                    <Space>
                      <ExperimentOutlined />
                      <span>{model.name}</span>
                    </Space>
                  }>
                    <Paragraph>
                      {model.explanation}
                    </Paragraph>
                    <Paragraph>
                      <Text strong>Formula: </Text>
                      <code>{model.formula}</code>
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>
        
        <div style={{ marginBottom: 24 }}>
          <Text strong>Select OTR Target:</Text>
          <Row gutter={16} align="middle">
            <Col span={18}>
              <Slider
                min={30}
                max={99}
                step={5}
                value={selectedOTR}
                onChange={setSelectedOTR}
                marks={{
                  80: '80%',
                  85: '85%',
                  90: '90%',
                  95: '95%',
                  99: '99%'
                }}
              />
            </Col>
            <Col span={6}>
              <InputNumber
                min={30}
                max={99}
                step={5}
                value={selectedOTR}
                onChange={setSelectedOTR}
                style={{ width: 100 }}
                addonAfter="%"
              />
            </Col>
          </Row>
        </div>

        {summaries.length > 0 && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {STATISTICAL_MODELS.map(model => {
              // Calculate summary for this model
              const modelSummary = summaries.map(summary => {
                // Get the calculated adjustments for this model
                const calculatedAdjustments = summary.calculatedAdjustments.map(item => {
                  const stdDevMinutes = (item.stdDev || item.calculatedAdjustment.stdDev) / 60;
                  const targetPercentile = summary.threshold === 100 ? 0.001 : summary.threshold / 100;
                  const adjustmentMinutes = model.calculateAdjustment(
                    stdDevMinutes,
                    targetPercentile,
                    (model.id === 'normal' || model.id === 'empirical') ? (item.variancesMinutes || []) : undefined
                  );
                  
                  return {
                    ...item,
                    calculatedAdjustment: {
                      ...item.calculatedAdjustment,
                      adjustmentMinutes,
                      projectedOTR: summary.threshold,
                      stdDev: item.stdDev || item.calculatedAdjustment.stdDev
                    }
                  };
                });

                const totalStops = calculatedAdjustments.length;
                const stopsWithAdjustments = calculatedAdjustments.filter(item => 
                  item.calculatedAdjustment.adjustmentMinutes !== 0
                ).length;
                
                const avgAdjustment = calculatedAdjustments.reduce((sum, item) => 
                  sum + item.calculatedAdjustment.adjustmentMinutes, 0
                ) / totalStops;
                
                const maxAdjustment = Math.max(...calculatedAdjustments.map(item => 
                  Math.abs(item.calculatedAdjustment.adjustmentMinutes)
                ));
                
                let avgStdDev = 0;
                let hasStdDevData = false;
                
                if (calculatedAdjustments.some(item => item.calculatedAdjustment.stdDev !== undefined)) {
                  avgStdDev = calculatedAdjustments.reduce((sum, item) => {
                    if (item.calculatedAdjustment.stdDev !== undefined) {
                      hasStdDevData = true;
                      return sum + item.calculatedAdjustment.stdDev;
                    }
                    return sum;
                  }, 0) / totalStops;
                }

                return {
                  threshold: summary.threshold,
                  totalStops,
                  stopsWithAdjustments,
                  avgAdjustment,
                  maxAdjustment,
                  avgStdDev,
                  hasStdDevData,
                  adjustmentPercentage: (stopsWithAdjustments / totalStops) * 100,
                  calculatedAdjustments
                };
              });

              return (
                <Col span={24} key={model.id}>
                  <Card size="small" title={
                    <Space>
                      <span>Summary for {model.name} ({selectedOTR}% OTR Target)</span>
                    </Space>
                  }>
                    <Row gutter={16}>
                      <Col span={6}>
                        <Statistic 
                          title="Total Stops Analyzed" 
                          value={modelSummary[0]?.totalStops || 0} 
                          prefix={<EnvironmentOutlined />} 
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic 
                          title="Stops Requiring Adjustment" 
                          value={modelSummary[0]?.stopsWithAdjustments || 0} 
                          suffix={`(${modelSummary[0]?.adjustmentPercentage.toFixed(1)}%)`}
                          prefix={<FieldTimeOutlined />} 
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic 
                          title="Average Adjustment" 
                          value={Math.abs(modelSummary[0]?.avgAdjustment || 0).toFixed(1)} 
                          suffix="minutes"
                          prefix={<ClockCircleOutlined />} 
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic 
                          title="Average Std Dev" 
                          value={modelSummary[0]?.hasStdDevData ? Math.round(modelSummary[0]?.avgStdDev / 60) : 'N/A'} 
                          suffix={modelSummary[0]?.hasStdDevData ? "minutes" : ""}
                          prefix={<BarChartOutlined />} 
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
        
        <Tabs defaultActiveKey="serviceInsights" type="card">
          <TabPane tab="Service Insights" key="serviceInsights">
            <HybridAnalysis
              reportData={reportData}
              proposedTimetables={proposedTimetables}
              selectedRoute={selectedRoute}
              selectedTrip={selectedTrip}
              setSelectedRoute={setSelectedRoute}
              setSelectedTrip={setSelectedTrip}
              selectedOTR={selectedOTR}
              STATISTICAL_MODELS={STATISTICAL_MODELS}
            />
          </TabPane>
          
          <TabPane tab="Proposed Adjustments" key="timetables">
            <div style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Select 
                    value={selectedRoute} 
                    onChange={value => { setSelectedRoute(value); setSelectedTrip('all'); }}
                    style={{ width: '100%' }}
                    placeholder="Select route"
                  >
                    <Option value="all">All Routes</Option>
                    {routes.map(route => (
                      <Option key={route} value={route}>Route {route}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  {selectedRoute !== 'all' && (() => {
                    const routeTimetables = proposedTimetables.proposedTimetables.filter(
                      item => item.routeNo === selectedRoute && item.tripId
                    );
                    const allTripIds = Array.from(
                      new Set(
                        routeTimetables
                          .flatMap(item => (item.tripId ? item.tripId.split(',') : []))
                          .filter(Boolean)
                      )
                    );
                    const tripOptions = allTripIds.map(tripId => {
                      const stopsForTrip = routeTimetables.filter(item =>
                        item.tripId && item.tripId.split(',').includes(tripId)
                      );
                      const earliest = stopsForTrip.reduce((min, stop) => {
                        if (!min) return stop;
                        return stop.scheduledTime < min.scheduledTime ? stop : min;
                      }, null);
                      let startTime = '';
                      if (earliest && earliest.scheduledTime) {
                        const timePart = earliest.scheduledTime.split(' ')[1];
                        if (timePart) {
                          const [h, m] = timePart.split(':');
                          startTime = `${h}:${m}`;
                        }
                      }
                      return { tripId, label: `Trip ${tripId} (${startTime})`, startTime };
                    });
                    const sortedTripOptions = tripOptions.sort((a, b) => {
                      const [aH, aM] = a.startTime.split(':').map(Number);
                      const [bH, bM] = b.startTime.split(':').map(Number);
                      return (aH * 60 + aM) - (bH * 60 + bM);
                    });
                    return (
                      <Select
                        value={selectedTrip}
                        onChange={value => {
                          const selected = sortedTripOptions.find(opt => opt.tripId === value);
                          setSelectedTrip(value);
                        }}
                        style={{ width: '100%' }}
                        placeholder="Select trip"
                      >
                        <Option value="all">All Trips</Option>
                        {sortedTripOptions.map(opt => (
                          <Option key={opt.tripId} value={opt.tripId}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    );
                  })()}
                </Col>
              </Row>
            </div>
            
            {thresholds.map(threshold => {
              // Filter by selected trip if one is selected
              let filteredByTrip = filteredTimetables;
              if (selectedTrip !== 'all') {
                filteredByTrip = filteredTimetables.filter(item => 
                  item.tripId && item.tripId.split(',').includes(selectedTrip)
                );
              }

              // Sort by impact (absolute value of adjustment)
              const sortedTimetables = [...filteredByTrip].sort((a, b) => {
                const aAdjustment = a.proposedAdjustments.find(adj => adj.target === threshold)?.adjustmentMinutes || 0;
                const bAdjustment = b.proposedAdjustments.find(adj => adj.target === threshold)?.adjustmentMinutes || 0;
                
                return sortByImpact 
                  ? Math.abs(bAdjustment) - Math.abs(aAdjustment)
                  : Math.abs(aAdjustment) - Math.abs(bAdjustment);
              });
              
              return (
                <div key={threshold} style={{ marginBottom: 24 }}>
                  <Title level={4}>{threshold}% OTR Target</Title>
                  {sortedTimetables.length > 0 ? (
                    <Table 
                      dataSource={sortedTimetables} 
                      columns={getColumnsForThreshold(threshold)} 
                      rowKey={(record) => `${record.routeNo}-${record.stopId}-${threshold}`}
                      pagination={selectedRoute === 'all' && selectedTrip === 'all' ? { pageSize: 5 } : false}
                      scroll={{ x: 1000 }}
                      defaultSortOrder="ascend"
                      defaultSortField="scheduledTime"
                      onRow={(record) => ({
                        onClick: () => {
                          setSelectedRoute(record.routeNo);
                          // If the record has a tripId, set it as the selected trip
                          if (record.tripId) {
                            const tripIds = record.tripId.split(',');
                            if (tripIds.length > 0) {
                              setSelectedTrip(tripIds[0]);
                            }
                          }
                        },
                        style: { cursor: 'pointer' }
                      })}
                    />
                  ) : (
                    <Empty description="No timetable adjustments found for the selected filters" />
                  )}
                </div>
              );
            })}
          </TabPane>
          
          <TabPane tab="Shift Envelope Analysis" key="shiftEnvelope">
            <Alert
              message="Shift Envelope Analysis"
              description={
                <Paragraph>
                  This analysis shows the operational impact of proposed timetable adjustments on driver shifts. 
                  Positive values indicate shifts would need to be extended, negative values indicate shifts could be shortened.
                  The cost implications are calculated based on the hourly rate and the total adjustment time.
                </Paragraph>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <div style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Select 
                    value={selectedRoute} 
                    onChange={value => { setSelectedRoute(value); setSelectedTrip('all'); }}
                    style={{ width: '100%' }}
                    placeholder="Select route"
                  >
                    <Option value="all">All Routes</Option>
                    {routes.map(route => (
                      <Option key={route} value={route}>Route {route}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  {selectedRoute !== 'all' && (() => {
                    const routeTimetables = proposedTimetables.proposedTimetables.filter(
                      item => item.routeNo === selectedRoute && item.tripId
                    );
                    const allTripIds = Array.from(
                      new Set(
                        routeTimetables
                          .flatMap(item => (item.tripId ? item.tripId.split(',') : []))
                          .filter(Boolean)
                      )
                    );
                    const tripOptions = allTripIds.map(tripId => {
                      const stopsForTrip = routeTimetables.filter(item =>
                        item.tripId && item.tripId.split(',').includes(tripId)
                      );
                      const earliest = stopsForTrip.reduce((min, stop) => {
                        if (!min) return stop;
                        return stop.scheduledTime < min.scheduledTime ? stop : min;
                      }, null);
                      let startTime = '';
                      if (earliest && earliest.scheduledTime) {
                        const timePart = earliest.scheduledTime.split(' ')[1];
                        if (timePart) {
                          const [h, m] = timePart.split(':');
                          startTime = `${h}:${m}`;
                        }
                      }
                      return { tripId, label: `Trip ${tripId} (${startTime})`, startTime };
                    });
                    const sortedTripOptions = tripOptions.sort((a, b) => {
                      const [aH, aM] = a.startTime.split(':').map(Number);
                      const [bH, bM] = b.startTime.split(':').map(Number);
                      return (aH * 60 + aM) - (bH * 60 + bM);
                    });
                    return (
                      <Select
                        value={selectedTrip}
                        onChange={value => {
                          const selected = sortedTripOptions.find(opt => opt.tripId === value);
                          setSelectedTrip(value);
                        }}
                        style={{ width: '100%' }}
                        placeholder="Select trip"
                      >
                        <Option value="all">All Trips</Option>
                        {sortedTripOptions.map(opt => (
                          <Option key={opt.tripId} value={opt.tripId}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    );
                  })()}
                </Col>
              </Row>
            </div>
            
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Card size="small" title="Cost Summary">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic 
                        title="Total Cost (95% OTR)" 
                        value={proposedTimetables.shiftEnvelope?.reduce((sum, shift) => {
                          const impact = calculateShiftEnvelopeImpact(shift);
                          return sum + (impact?.cost95 || 0);
                        }, 0).toFixed(2)} 
                        prefix="$"
                        suffix="per day"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic 
                        title="Total Cost (98% OTR)" 
                        value={proposedTimetables.shiftEnvelope?.reduce((sum, shift) => {
                          const impact = calculateShiftEnvelopeImpact(shift);
                          return sum + (impact?.cost98 || 0);
                        }, 0).toFixed(2)} 
                        prefix="$"
                        suffix="per day"
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
            
            {proposedTimetables.shiftEnvelope && proposedTimetables.shiftEnvelope.length > 0 ? (
              <Table 
                dataSource={proposedTimetables.shiftEnvelope} 
                columns={shiftColumns} 
                rowKey="shiftId"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1000 }}
              />
            ) : (
              <Empty description="No shift envelope data available" />
            )}
          </TabPane>
        </Tabs>
        
        <Divider />
        
        <Collapse ghost>
          <Panel header="How are these adjustments calculated?" key="1">
            <Paragraph>
              The proposed timetable adjustments are calculated using statistical analysis of historical performance data:
            </Paragraph>
            <ol>
              <li>For each route-stop combination, we calculate the mean and standard deviation of arrival/departure variances.</li>
              <li>We use multiple statistical models to analyze the distribution of arrival/departure times:
                <ul>
                  {STATISTICAL_MODELS.map(model => (
                    <li key={model.id}>
                      <strong>{model.name}:</strong> {model.explanation}
                      <br />
                      <code>{model.formula}</code>
                    </li>
                  ))}
                </ul>
              </li>
              <li>To achieve a specific on-time performance target (e.g., {selectedOTR}%), we adjust the schedule to the corresponding percentile of the variance distribution.</li>
              <li>For example, to achieve {selectedOTR}% on-time performance, we adjust to the {(100 - selectedOTR).toFixed(1)}th percentile.</li>
              <li>The "Shift Envelope" analysis aggregates these adjustments by shift to show the operational impact.</li>
            </ol>
            <Paragraph>
              <Text type="secondary">
                Note: These are statistical recommendations based on historical data. Actual implementation may require additional operational considerations.
              </Text>
            </Paragraph>
          </Panel>
        </Collapse>
      </Card>
      
      {renderDistributionModal()}
    </div>
  );
};

export default ProposedTimetables; 